/**
 * LeadContext Object - Similar to CRM frontend LeadContext
 * Creates a comprehensive lead management object with all API endpoints and utilities
 */

import React from 'react';
import type { InstallationSettingsVertical } from '../../core/settings/installation-settings';
import { AuthContext } from '../auth/context';
import { resolveScopeFromDesignId } from './canvasDesignTree';

export interface LeadContextValue {
  // Core data
  user: any;
  company: string | undefined;
  uid: string;
  tab: string;
  designVertical: InstallationSettingsVertical;
  lead: any | null;
  AiDesigns: any;

  // State management
  setUtility: (utility: any) => void;
  validUtility: boolean;
  navigateTab: (tab: string) => void;
  updateLead: (data: any) => Promise<any>;
  refreshLead: (field?: string) => void;
  stepsDone: string[];
  stepsDoneHandler: (step: string) => void;

  // API Endpoints
  leadAPI: any;
  noteAPI: any;
  taskAPI: any;
  documentAPI: any;
  proposalAPI: any;
  CanvasAPI: any;
  hardwareAPI: any;
  packageAPI: any;
  productionMapAPI: any;
  saveShadingsAPI: any;
  removeShadingsAPI: any;
  runShadingsAPI: any;
  runShadingRequiredClientDataAPI: any;
  leadImagesAPI: any;
  thumbnailsAPI: any;
  exportDesignAPI: any;
  fieldsAPI: any;
  coverageAPI: any;
  namevaluesAPI: any;
  pinHouse: (data: any) => Promise<any>;
  predictAPI: any;
  PVWattsAPI: any;
  PVWattsHourlyAPI: any;
  StringingAPI: any;
  projectAPI: any;
  paymentAPI: any;
  measureAPI: any;
  optimizeAPI: any;
  edgeDetectionAPI: any;
  rectificationAPI: any;
  panelPlacementAPI: any;
  canvasImportDxfAPI: any;
  ratesAPI: any;
  solarProductionDesignAPI: any;
  obstacleTunePredictAPI: any;
  treePredictAPI: any;

  // Utility functions
  getTaskStatus: (taskId: string) => Promise<any>;
  displaySnack: (state: any, time?: number) => void;
  newEndpoint: (path: string, blob?: boolean) => any;
  getHeaders: () => { Authorization?: string };
  getBaseUrl: () => string;
  auth: any;
  iframe: boolean;
  retryConnection: () => void;
}

/**
 * Create LeadContext object similar to CRM frontend
 * @param uid - Lead UID
 * @param authContext - AuthContext from the app
 * @param leadData - Lead data from API (debug.txt structure)
 * @param options.initialCanvasId - designer/canvas row id; omit to use the canvas with is_default
 * @returns LeadContext object
 */
export const createLeadContext = (
  uid: string,
  authContext: React.ContextType<typeof AuthContext>,
  leadData: any,
  options?: {
    initialCanvasId?: string | null;
    designVertical?: InstallationSettingsVertical;
  }
): LeadContextValue => {
  // Lead state
  let lead = leadData;
  const explicitCanvasId =
    typeof options?.initialCanvasId === 'string'
      ? options.initialCanvasId.trim()
      : '';
  let canvasTargetId = explicitCanvasId || uid;
  let canvasDesignTargetId = explicitCanvasId || uid;
  let canvasTargetResolved = false;
  let explicitDesignSelection = false;
  let utility = leadData?.utility || null;
  let stepsDone = ['Customer Details'];

  // Utility validation
  const validUtility =
    lead?.utility?.usage !== undefined &&
    lead?.utility?.rate_amount !== undefined &&
    lead?.utility?.fixed_charge !== undefined;

  // State management functions
  const setUtility = (newUtility: any) => {
    utility = newUtility;
    if (lead) {
      lead.utility = newUtility;
    }
  };

  const updateLead = async (updateData: any) => {
    const response = await leadAPI.update(uid, updateData);
    lead = { ...lead, ...response };
    return response;
  };

  const refreshLead = async (field?: string) => {
    const data = await leadAPI.retrieve(uid);
    if (field) {
      lead = { ...lead, [field]: data[field] };
    } else {
      lead = data;
      utility = data.utility;
    }
    return data;
  };

  const navigateTab = (newTab: string) => {
    console.log(`Navigate to tab: ${newTab}`);
    // Implementation depends on routing needs
  };

  const stepsDoneHandler = (newStep: string) => {
    stepsDone = [newStep, ...stepsDone];
  };

  // PinHouse function
  const pinHouse = async (data: any) => {
    return await pinHouseAPI.create(data);
  };

  // Create all API endpoints
  const leadAPI = authContext.newEndpoint('crm/leads/');
  const noteAPI = authContext.newEndpoint('crm/notes/');
  const taskAPI = authContext.newEndpoint('crm/tasks/');
  const documentAPI = authContext.newEndpoint('crm/documents/');
  const proposalAPI = authContext.newEndpoint('designer/proposal/');
  const designVertical = options?.designVertical ?? 'solar';
  const isRoofing = designVertical === 'roofing';
  const packageAPI = authContext.newEndpoint(
    isRoofing ? 'roofing/installation-packages/' : 'hardware/packages/'
  );
  const panelsAPI = isRoofing
    ? null
    : authContext.newEndpoint('hardware/panels/');
  const invertersAPI = isRoofing
    ? null
    : authContext.newEndpoint('hardware/inverters/');
  const roofingMaterialsAPI = isRoofing
    ? authContext.newEndpoint('roofing/materials/')
    : null;
  const roofingLaborAPI = isRoofing
    ? authContext.newEndpoint('roofing/labor/')
    : null;
  const hardwareAPI = {
    list: async () => {
      if (isRoofing) {
        const [materialsRes, laborRes, packagesRes] = await Promise.all([
          roofingMaterialsAPI!.list(),
          roofingLaborAPI!.list(),
          packageAPI.list()
        ]);
        const toArray = (r: any) => (Array.isArray(r) ? r : (r?.results ?? []));
        return {
          panels: [],
          inverters: [],
          packages: toArray(packagesRes),
          materials: toArray(materialsRes),
          labor: toArray(laborRes),
          company_id: '',
          company_name: ''
        };
      }
      const [panelsRes, invertersRes, packagesRes] = await Promise.all([
        panelsAPI!.list(),
        invertersAPI!.list(),
        packageAPI.list()
      ]);
      const toArray = (r: any) => (Array.isArray(r) ? r : (r?.results ?? []));
      return {
        panels: toArray(panelsRes),
        inverters: toArray(invertersRes),
        packages: toArray(packagesRes),
        company_id: '',
        company_name: ''
      };
    }
  };
  const productionMapAPI = authContext.newEndpoint('crm/production/');
  const saveShadingsAPI = authContext.newEndpoint('designer/gradients/');
  const removeShadingsAPI = authContext.newEndpoint(
    'designer/gradients/remove/'
  );
  const runShadingsAPI = authContext.newEndpoint('designer/gradients/shade');
  const runShadingRequiredClientDataAPI = authContext.newEndpoint(
    'designer/gradients/shading_required_client_data'
  );
  const leadImagesAPI = authContext.newEndpoint('crm/lead/images/');
  const thumbnailsAPI = authContext.newEndpoint('crm/lead/thumbnails/');
  // DXF export returns binary data, so we need responseType: 'blob'
  const exportDesignAPI = authContext.newEndpoint(
    'designer/export/design/',
    true
  );
  const fieldsAPI = authContext.newEndpoint('crm/lead/fields/');
  const coverageAPI = authContext.newEndpoint('api/coverage/');
  const namevaluesAPI = authContext.newEndpoint('crm/lead/namevalues/');
  const pinHouseAPI = authContext.newEndpoint('crm/lead/pin_house/');
  const predictAPI = authContext.newEndpoint('crm/lead/predict/');
  const PVWattsAPI = authContext.newEndpoint('designer/pvwatts/');
  const PVWattsHourlyAPI = authContext.newEndpoint('designer/pvwatts/hourly/');
  const StringingAPI = authContext.newEndpoint('designer/string/auto/');
  const projectAPI = authContext.newEndpoint('project_management/projects/');
  const paymentAPI = authContext.newEndpoint('project_management/payments/');
  const measureAPI = authContext.newEndpoint('designer/measure/');
  const optimizeAPI = authContext.newEndpoint('designer/optimize/');
  const edgeDetectionAPI = authContext.newEndpoint('designer/edge_detection/');
  const rectificationAPI = authContext.newEndpoint('designer/rectify/');
  const panelPlacementAPI = authContext.newEndpoint(
    'designer/panel_placement/'
  );
  const canvasImportDxfAPI = authContext.newEndpoint('designer/import/dxf/');
  const ratesAPI = authContext.newEndpoint('hardware/rates/');
  const solarProductionDesignAPI = authContext.newEndpoint(
    'designer/solar-production/design/'
  );
  const obstacleTunePredictAPI = authContext.newEndpoint(
    'designer/predict/obstacles/tune/'
  );
  const treePredictAPI = authContext.newEndpoint('designer/predict/trees/');

  // Canvas API implementation (similar to CRM frontend hooks/canvas.js)
  const CanvasAPI = (() => {
    const canvasAPI = authContext.newEndpoint('designer/canvas');
    const canvasesByLeadAPI = authContext.newEndpoint(
      'designer/canvas/by_lead/'
    );

    // Helper to load data from API
    // NOTE: package data is preserved from previous canvasData since backend Canvas doesn't return it
    const loadData = (data: any, previousCanvasData?: any) => {
      const shade = data.shaded;
      const colorRange = data.color_range?.split(',').map(Number) || [];
      const panels = data.panels || [];
      const annotations = data.annotations || [];

      // Get segments data
      const segments: any[] = [];
      if (data.segments) {
        data.segments.forEach((segment: any) => {
          const {
            lines,
            height,
            azimuth,
            panel_orientation: panelOrientation,
            ac_monthly: acMonthly,
            poa_monthly: poaMonthly,
            solar_access: solarAccess,
            ...rest
          } = segment;

          const lineArray =
            lines && lines.length > 0
              ? lines
              : Array(segment.geometry?.length || 0).fill('');
          const acAnnual = acMonthly
            ? acMonthly.reduce((a: number, b: number) => a + b, 0)
            : 0;

          if (segment.geometry && segment.geometry.length > 2) {
            segments.push({
              ...rest,
              shape: 'Polygon',
              height: +height,
              azimuth: +azimuth,
              lines: lineArray.map((lineLabel: string) =>
                lineLabel === 'None' ? '' : lineLabel
              ),
              panelOrientation,
              acMonthly: acMonthly || [],
              poaMonthly: poaMonthly || [],
              acAnnual,
              solarAccess: solarAccess || []
            });
          }
        });
      }

      // Get obstacle data
      const obstacles = (data.obstacles || []).map(
        ({ radius, height, shape, ...obstacle }: any) => ({
          ...obstacle,
          height: +height,
          radius: shape === 'Polygon' ? null : +radius,
          shape
        })
      );

      // Get tree data
      const trees = (data.trees || []).map(
        ({ radius, height, ...tree }: any) => ({
          ...tree,
          height: +height
        })
      );

      // 🎯 Design Tool logic: Use saved hardware from canvas if available, else preserve previous package
      // Backend returns selected_panel and selected_inverter (full objects); use them to build package
      const selectedPanel = data.selected_panel ?? null;
      const selectedInverter = data.selected_inverter ?? null;
      const packageData =
        previousCanvasData?.package ??
        (selectedPanel ||
        selectedInverter ||
        data.inverter_quantity != null ||
        data.additional_inverters != null
          ? {
              ...(previousCanvasData?.package || {}),
              ...(selectedPanel ? { panel: selectedPanel } : {}),
              ...(selectedInverter ? { inverter: selectedInverter } : {}),
              ...(data.inverter_quantity != null
                ? { inverterQuantity: data.inverter_quantity }
                : {}),
              ...(data.additional_inverters != null
                ? { additional_inverters: data.additional_inverters }
                : {})
            }
          : undefined);
      // Pass through IDs for forms that resolve from local hardware lists
      const selectedPanelId =
        data.selected_panel_id ?? selectedPanel?.id ?? null;
      const selectedInverterId =
        data.selected_inverter_id ?? selectedInverter?.id ?? null;

      // Preserve production/stringing fields so panels-from-stringing selection works after refresh (mirror DesignTool)
      const sameDesign =
        !previousCanvasData?.design_id ||
        !data?.design_id ||
        String(previousCanvasData.design_id) === String(data.design_id);
      const stringingData =
        data.stringing_data !== undefined
          ? data.stringing_data
          : sameDesign
            ? (previousCanvasData?.stringing_data ?? null)
            : null;
      const stringingRoofIndexMap =
        data.stringing_roof_index_map !== undefined
          ? data.stringing_roof_index_map
          : sameDesign
            ? (previousCanvasData?.stringing_roof_index_map ?? null)
            : null;
      const stringingSelectedPanelIndexMap =
        data.stringing_selected_panel_index_map !== undefined
          ? data.stringing_selected_panel_index_map
          : sameDesign
            ? (previousCanvasData?.stringing_selected_panel_index_map ?? null)
            : null;
      const stringingPanelIndexMode =
        data.stringing_panel_index_mode !== undefined
          ? data.stringing_panel_index_mode
          : sameDesign
            ? (previousCanvasData?.stringing_panel_index_mode ?? 'all')
            : 'all';

      return {
        id: data.id ?? data.pk ?? null,
        pk: data.pk ?? data.id ?? null,
        design_id: data.design_id ?? null,
        canvas_id: data.canvas_id ?? null,
        system_size_kw: data.system_size_kw ?? null,
        total_annual_kwh: data.total_annual_kwh ?? null,
        total_monthly_kwh: data.total_monthly_kwh ?? null,
        total_clipping_kwh: data.total_clipping_kwh ?? null,
        total_monthly_clipping_kwh: data.total_monthly_clipping_kwh ?? null,
        dc_ac_ratio: data.dc_ac_ratio ?? null,
        mppt_count_total: data.mppt_count_total ?? null,
        inverter_count_total: data.inverter_count_total ?? null,
        inverters_used: data.inverters_used ?? null,
        production_per_panel: data.production_per_panel ?? null,
        production_per_string: data.production_per_string ?? null,
        hourly_production: data.hourly_production ?? null,
        only_selected_panels: data.only_selected_panels ?? null,
        selected_panel_count: data.selected_panel_count ?? null,
        offset_percentage: data.offset_percentage ?? null,
        annual_usage_kwh: data.annual_usage_kwh ?? null,
        segments,
        obstacles,
        trees,
        shade,
        colorRange,
        panels,
        annotations,
        ...('shading_images' in data
          ? { shading_images: data.shading_images }
          : {}),
        rgb: data.rgb ?? null,
        dsm: data.dsm ?? null,
        dsm_params: data.dsm_params ?? null,
        resolution: data.resolution ?? null,
        ...(packageData ? { package: packageData } : {}),
        selected_panel: selectedPanel,
        selected_inverter: selectedInverter,
        selected_panel_id: selectedPanelId,
        selected_inverter_id: selectedInverterId,
        ...(stringingData != null ? { stringing_data: stringingData } : {}),
        ...(stringingRoofIndexMap != null
          ? { stringing_roof_index_map: stringingRoofIndexMap }
          : {}),
        ...(stringingSelectedPanelIndexMap != null
          ? {
              stringing_selected_panel_index_map: stringingSelectedPanelIndexMap
            }
          : {}),
        ...(stringingPanelIndexMode !== undefined
          ? { stringing_panel_index_mode: stringingPanelIndexMode }
          : {}),
        ...('checklist' in data
          ? { checklist: data.checklist }
          : previousCanvasData?.checklist !== undefined
            ? { checklist: previousCanvasData.checklist }
            : {})
      };
    };

    const resolveCanvasTargetId = async () => {
      if (canvasTargetResolved) return canvasTargetId;
      canvasTargetResolved = true;
      try {
        const data = await canvasesByLeadAPI.list(
          `?lead=${encodeURIComponent(uid)}${
            explicitCanvasId ? '&include_archived=true' : ''
          }`
        );
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];
        const explicitScope = explicitCanvasId
          ? resolveScopeFromDesignId(items, explicitCanvasId)
          : null;
        if (explicitScope) {
          canvasTargetId = explicitScope.canvasId;
          canvasDesignTargetId = explicitScope.designId;
          explicitDesignSelection = true;
        } else {
          const preferred =
            items.find((item: any) => item?.is_default && !item?.archived_at) ??
            items.find((item: any) => !item?.archived_at);
          const id = preferred?.id ?? preferred?.pk;
          if (id) {
            const preferredScope = resolveScopeFromDesignId(items, String(id));
            canvasTargetId = preferredScope?.canvasId ?? String(id);
            canvasDesignTargetId = preferredScope?.designId ?? String(id);
          }
        }
      } catch (error) {
        void error;
      }
      return canvasTargetId;
    };

    const getCanvas = (currentCanvasData?: any) => {
      return resolveCanvasTargetId().then(() => {
        const query =
          canvasDesignTargetId &&
          (explicitDesignSelection ||
            String(canvasDesignTargetId) !== String(canvasTargetId))
            ? `${canvasTargetId}?design_id=${encodeURIComponent(canvasDesignTargetId)}`
            : canvasTargetId;
        return canvasAPI.retrieve(query).then((data: any) => {
          if (data?.design_id) {
            canvasDesignTargetId = String(data.design_id);
          }
          if (data?.canvas_id) {
            canvasTargetId = String(data.canvas_id);
            canvasTargetResolved = true;
          }
          return loadData(data, currentCanvasData);
        });
      });
    };

    // Save all data. options.partial === true uses PATCH (mirror DesignTool saveProductionToCanvas).
    const saveCanvas = (
      canvasData: any,
      options?: {
        partial?: boolean;
        canvasChanged?: boolean;
        confirmArchiveSiblings?: boolean;
        updateDefaults?: boolean;
        updateLeadDesignDefault?: boolean;
      }
    ) => {
      try {
        // 🔧 CRITICAL: Preserve package data before save (backend doesn't support it)
        const packageData = canvasData.package;

        const {
          segments,
          obstacles,
          trees,
          colorRange,
          package: _packageData,
          ...canvas
        } = canvasData;

        if (segments) {
          canvas.segments = segments.map(
            ({
              acMonthly,
              poaMonthly,
              solarAccess,
              panelOrientation,
              tsrf,
              tof,
              azimuth,
              pitch,
              height,
              ...segment
            }: any) => {
              // Default arrays for required fields (13 months: Jan-Dec + Year)
              const defaultArray = Array(13).fill(0);
              const defaultAccessArray = Array(13).fill('1.0000');

              // Helper to normalize numeric fields (handle both string and number types)
              const normalizeNumeric = (value: any) => {
                if (value == null) return value;
                const num =
                  typeof value === 'number' ? value : parseFloat(value);
                return isNaN(num) ? value : parseFloat(num.toFixed(2));
              };

              return {
                ...segment,
                // Round numeric fields to appropriate precision to avoid backend validation errors
                azimuth: normalizeNumeric(azimuth), // Max 5 total digits: xxx.xx
                pitch: normalizeNumeric(pitch),
                height: normalizeNumeric(height),
                panel_orientation: normalizeNumeric(panelOrientation),
                // Ensure required array fields have values (use defaults if missing)
                ac_monthly:
                  acMonthly && acMonthly.length > 0 ? acMonthly : defaultArray,
                poa_monthly:
                  poaMonthly && poaMonthly.length > 0
                    ? poaMonthly
                    : defaultArray,
                solar_access:
                  solarAccess && solarAccess.length > 0
                    ? solarAccess.map((val: number) => val?.toFixed(4))
                    : defaultAccessArray,
                tsrf:
                  tsrf && tsrf.length > 0
                    ? tsrf.map((val: number) => val?.toFixed(4))
                    : defaultAccessArray,
                tof:
                  tof && tof.length > 0
                    ? tof.map((val: number) => val?.toFixed(4))
                    : defaultAccessArray
              };
            }
          );
        }
        if (obstacles) {
          // Transform obstacles: normalize numeric fields to backend format
          canvas.obstacles = obstacles.map(
            ({ height, radius, setback, ...obstacle }: any) => {
              // Helper to normalize numeric fields (handle both string and number types)
              const normalizeNumeric = (value: any) => {
                if (value == null) return value;
                const num =
                  typeof value === 'number' ? value : parseFloat(value);
                return isNaN(num) ? value : parseFloat(num.toFixed(2));
              };

              const transformed = {
                ...obstacle,
                // Normalize numeric fields to 2 decimal places
                height: normalizeNumeric(height),
                radius: normalizeNumeric(radius),
                setback: normalizeNumeric(setback)
              };

              // Log transformation for first obstacle
              if (obstacle.id === obstacles[0].id) {
                console.log('💾 leadContext - Transforming first obstacle:', {
                  before: { height, radius, setback },
                  after: {
                    height: transformed.height,
                    radius: transformed.radius,
                    setback: transformed.setback
                  },
                  types: {
                    heightBefore: typeof height,
                    heightAfter: typeof transformed.height,
                    radiusBefore: typeof radius,
                    radiusAfter: typeof transformed.radius
                  }
                });
              }

              return transformed;
            }
          );
        }
        if (trees) {
          canvas.trees = trees;
        }
        if (colorRange) {
          canvas.color_range = colorRange.join(',');
        }

        // 🔧 NOTE: Package data (inverter_quantity, additional_inverters) is saved separately
        // via installation settings API, not via canvas API. The backend Canvas model doesn't
        // have a package field. Package changes should trigger a separate save to installation settings.

        const usePatch = options?.partial === true;
        if (canvasDesignTargetId) {
          canvas.design_id = canvasDesignTargetId;
        }
        if (canvasData?.roofing_data !== undefined) {
          canvas.roofing_data = canvasData.roofing_data;
        }
        if (options?.canvasChanged === true) canvas.canvas_changed = true;
        if (options?.canvasChanged === false) canvas.canvas_changed = false;
        if (options?.confirmArchiveSiblings) {
          canvas.confirm_archive_siblings = true;
        }
        if (options?.updateDefaults) {
          canvas.update_defaults = true;
          canvas.update_lead_design_default = Boolean(
            options.updateLeadDesignDefault
          );
        }
        return resolveCanvasTargetId().then(() =>
          canvasAPI
            .update(canvasTargetId, canvas, usePatch)
            .then((data: any) => {
              const loadedData = loadData(data, canvasData);
              return loadedData;
            })
            .catch((err: any) => {
              const status = err?.response?.status;
              const detail = err?.response?.data?.detail || err?.response?.data;
              if (
                status === 409 ||
                detail?.code === 'canvas_sibling_archive_required'
              ) {
                const conflict = new Error('canvas_sibling_archive_required');
                (
                  conflict as Error & { designs?: unknown; status?: number }
                ).designs = detail?.designs || [];
                (conflict as Error & { status?: number }).status = 409;
                return Promise.reject(conflict);
              }
              return Promise.reject(err);
            })
        );
      } catch (error) {
        console.error('❌ saveCanvas - Error preparing data:', error);
        return Promise.reject(error);
      }
    };

    return {
      retrieveCanvas: (canvasId: string, designId?: string) => {
        const query = designId
          ? `${canvasId}?design_id=${encodeURIComponent(String(designId))}`
          : String(canvasId);
        return canvasAPI.retrieve(query);
      },
      saveCanvas,
      getCanvas,
      getCanvasTargetId: () => (canvasTargetResolved ? canvasTargetId : ''),
      getCanvasDesignTargetId: () => canvasDesignTargetId,
      setCanvasTargetId: (id: string, designId?: string) => {
        canvasTargetId = String(id);
        canvasDesignTargetId = String(designId || id);
        explicitDesignSelection = designId != null && String(designId) !== '';
        canvasTargetResolved = true;
      },
      listCanvasDesignsForLead: async (options?: {
        includeArchived?: boolean;
      }) => {
        const search = options?.includeArchived
          ? `?lead=${encodeURIComponent(uid)}&include_archived=true`
          : `?lead=${encodeURIComponent(uid)}`;
        const data = await canvasesByLeadAPI.list(search);
        return Array.isArray(data) ? data : [];
      },
      archiveCanvas: async (canvasId: string) => {
        const archiveAPI = authContext.newEndpoint(
          `designer/canvas/${encodeURIComponent(String(canvasId))}/archive/`
        );
        return archiveAPI.create({});
      },
      restoreCanvas: async (canvasId: string) => {
        const restoreAPI = authContext.newEndpoint(
          `designer/canvas/${encodeURIComponent(String(canvasId))}/restore/`
        );
        return restoreAPI.create({});
      },
      archiveDesign: async (canvasId: string, designId: string) => {
        const archiveAPI = authContext.newEndpoint(
          `designer/canvas/${encodeURIComponent(String(canvasId))}/designs/${encodeURIComponent(String(designId))}/archive/`
        );
        return archiveAPI.create({});
      },
      restoreDesign: async (canvasId: string, designId: string) => {
        const restoreAPI = authContext.newEndpoint(
          `designer/canvas/${encodeURIComponent(String(canvasId))}/designs/${encodeURIComponent(String(designId))}/restore/`
        );
        return restoreAPI.create({});
      },
      cloneCanvas: async (options?: {
        name?: string;
        designName?: string;
        target?: 'canvas' | 'design';
        designId?: string;
        state?: Record<string, unknown>;
        saveAs?: boolean;
        updateLeadDesignDefault?: boolean;
      }) => {
        const sourceId = await resolveCanvasTargetId();
        const action = options?.saveAs ? 'save-as' : 'clone';
        const cloneAPI = authContext.newEndpoint(
          `designer/canvas/${sourceId}/${action}/`
        );
        return cloneAPI.create({
          name: options?.name,
          design_name: options?.designName,
          target: options?.target || 'canvas',
          design_id: options?.designId || canvasDesignTargetId,
          state: options?.state,
          update_lead_design_default: Boolean(options?.updateLeadDesignDefault)
        });
      },
      renameCanvas: async (canvasId: string, name: string) => {
        return canvasAPI.update(String(canvasId), { name }, true);
      },
      renameDesign: async (
        canvasId: string,
        designId: string,
        name: string
      ) => {
        const designAPI = authContext.newEndpoint(
          `designer/canvas/${encodeURIComponent(String(canvasId))}/designs/`
        );
        return designAPI.update(
          encodeURIComponent(String(designId)),
          { name },
          true
        );
      }
    };
  })();

  // Return the complete LeadContext object
  return {
    // Core data
    user: authContext.user,
    company: authContext.company,
    uid,
    tab: 'design-tool',
    designVertical,
    lead: lead, // Return actual lead data, not function
    AiDesigns: {}, // Placeholder - would fetch from API

    // State management
    setUtility,
    validUtility,
    navigateTab,
    updateLead,
    refreshLead,
    stepsDone,
    stepsDoneHandler,

    // All API endpoints
    leadAPI,
    noteAPI,
    taskAPI,
    documentAPI,
    proposalAPI,
    CanvasAPI,
    hardwareAPI,
    packageAPI,
    productionMapAPI,
    saveShadingsAPI,
    removeShadingsAPI,
    runShadingsAPI,
    runShadingRequiredClientDataAPI,
    leadImagesAPI,
    thumbnailsAPI,
    exportDesignAPI,
    fieldsAPI,
    coverageAPI,
    namevaluesAPI,
    pinHouse,
    predictAPI,
    PVWattsAPI,
    PVWattsHourlyAPI,
    StringingAPI,
    projectAPI,
    paymentAPI,
    measureAPI,
    optimizeAPI,
    edgeDetectionAPI,
    rectificationAPI,
    panelPlacementAPI,
    canvasImportDxfAPI,
    ratesAPI,
    solarProductionDesignAPI,
    obstacleTunePredictAPI,
    treePredictAPI,

    // Utility functions
    getTaskStatus: authContext.getTaskStatus,
    displaySnack: authContext.displaySnack,
    newEndpoint: authContext.newEndpoint,
    getHeaders: authContext.getHeaders,
    getBaseUrl: authContext.getBaseUrl,
    auth: authContext.auth,
    iframe: authContext.iframe,
    retryConnection: authContext.retryConnection
  };
};

export const cloneProposalAs = (
  authContext: any,
  proposalId: string,
  name: string
) => {
  const trimmed = String(name || '').trim();
  if (!trimmed) return Promise.reject(new Error('A name is required.'));
  const saveAsAPI = authContext.newEndpoint(
    `designer/proposal/${encodeURIComponent(String(proposalId))}/save-as/`
  );
  return saveAsAPI.create({ name: trimmed });
};
